import type { FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { Button } from '@packages/shared';
import type { GenericTrackingEvent } from '@packages/tracking';
import { useTracking } from '@packages/tracking';

import type { SelectedFilterValue } from '../../types';
import type { Filter } from '../../api/rest/types';
import { useFilterGroupValues } from '../../hooks/useFilterGroupValues';

export type FilterSubmitButtonProps = {
  /** If `true`, there are changes to be submitted */
  hasChanges?: boolean;
  /** Callback fired when a user cancels the changes */
  onCancel?: () => void;
  /** Callback fired when a user submits the changes */
  onSubmit?: () => void;
  /** If `true`, Button will be a fullWidth Button */
  fullWidth?: boolean;
  /** The current filter */
  filter?: Filter;
  /** The selected filter values array */
  selectedFilterValues?: SelectedFilterValue[];
};

const useFilterTrackingLabels = (
  hasChanges: boolean,
  selectedFilterValues: SelectedFilterValue[],
  filter?: Filter,
) => {
  const { response } = useFilterGroupValues(
    filter?.id ?? '',
    hasChanges && filter?.id !== undefined && filter?.id !== '',
  );

  if (!response || !('groupedValues' in response)) {
    return [''];
  }

  let selectedValueIDs: string[] = [];

  // eslint-disable-next-line array-callback-return, consistent-return
  selectedFilterValues.map((val) => {
    if (val.filterId === filter?.id) {
      selectedValueIDs = selectedValueIDs.concat(val.selectedValues);
    }
  });

  const labels: string[] = [];

  response.groupedValues
    .flatMap((x) => x.values)
    .forEach((x) => {
      if (selectedValueIDs.includes(x.id)) {
        labels.push(x.name);
      }
    });

  return labels;
};

const messages = defineMessages({
  cancel: {
    id: 'filters.details.mobile.cancelButton.text',
    defaultMessage: 'Schließen',
  },
  submit: {
    id: 'filters.details.mobile.submitButton.text',
    defaultMessage: 'Übernehmen',
  },
});

/**
 * FilterSubmitButton component description displayed in storybook
 * */
export const FilterSubmitButton: FC<FilterSubmitButtonProps> = ({
  hasChanges,
  onCancel,
  onSubmit,
  fullWidth,
  filter,
  selectedFilterValues,
}) => {
  const dispatchGTMEvent = useTracking();
  const trackingLabels = useFilterTrackingLabels(
    (hasChanges && filter !== undefined) ?? false,
    selectedFilterValues ?? [],
    filter,
  );

  if (hasChanges) {
    const onClick = () => {
      if (onSubmit) {
        dispatchGTMEvent<GenericTrackingEvent>({
          event: 'ga-event',
          eventAction: 'Filter / Sort benutzt',
          eventCategory: 'User Interactions',
          eventLabel: `${filter?.name}_top`,
          eventValue: '',
        });
        dispatchGTMEvent<GenericTrackingEvent>({
          event: 'suche-event',
          eventValue: `filter_set=${filter?.id}=${trackingLabels.join(',')}`,
        });
        onSubmit();
      }
    };

    return (
      <Button color="primary" onClick={onClick} fullWidth={fullWidth}>
        <FormattedMessage {...messages.submit} />
      </Button>
    );
  }
  return (
    <Button onClick={onCancel} fullWidth={fullWidth}>
      <FormattedMessage {...messages.cancel} />
    </Button>
  );
};
